/**
 * StreamingChatPanelComponent - Generic streaming AI chat panel.
 *
 * This component provides a full-featured chat interface for AI assistants
 * with support for streaming responses, thinking/reasoning display, tool calls,
 * and customizable actions.
 *
 * Features:
 * - Streaming message display with cursor
 * - Thinking/reasoning section (collapsible)
 * - Tool call visualization (inline, expandable)
 * - Message queueing when request in progress
 * - Auto-scroll to bottom
 * - Request cancellation
 * - New chat with confirmation
 * - Error handling with retry
 * - Customizable empty state with presets
 * - Customizable tool call labels and visibility
 */

import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  OnDestroy,
  TemplateRef,
  ContentChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, AlertController, IonTextarea } from '@ionic/angular';

import { MarkdownPipe } from '../ai-chat-panel/markdown.pipe';

import {
  StreamingChatMessage,
  StreamingChatMessageAction,
  StreamingChatPreset,
  StreamingChatEmptyState,
  StreamingChatPanelConfig,
  ToolCallInfo,
  HitlApprovalInfo,
  MessageActionClickEvent,
  SendMessagePayload,
} from './streaming-chat-panel.types';

// Re-export types for convenience
export * from './streaming-chat-panel.types';

@Component({
  selector: 'app-streaming-chat-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, MarkdownPipe],
  templateUrl: './streaming-chat-panel.component.html',
  styleUrls: ['./streaming-chat-panel.component.scss'],
})
export class StreamingChatPanelComponent implements AfterViewChecked, OnDestroy {
  // ============================================================================
  // Configuration Inputs
  // ============================================================================

  /** Panel visibility */
  @Input() isOpen = true;

  /** Panel configuration */
  @Input() config: StreamingChatPanelConfig = {};

  /** Empty state configuration (convenience input, overrides config.emptyState) */
  @Input() emptyState?: StreamingChatEmptyState;

  /** Presets for quick actions (convenience input, overrides emptyState.presets) */
  @Input() presets?: StreamingChatPreset[];

  // ============================================================================
  // State Inputs
  // ============================================================================

  /** Conversation history */
  @Input() messages: StreamingChatMessage[] = [];

  /** Currently streaming message (null when not streaming) */
  @Input() streamingMessage: StreamingChatMessage | null = null;

  /** Whether a request is in progress */
  @Input() isLoading = false;

  /** Session identifier */
  @Input() sessionId?: string;

  /** Queued message (shown when already loading) */
  @Input() queuedMessage?: string;

  // ============================================================================
  // Customization Inputs (Content Projection Slots)
  // ============================================================================

  /** Custom header content (replaces default header) */
  @ContentChild('headerSlot') headerSlotTemplate?: TemplateRef<unknown>;

  /** Custom actions below header (e.g., diff notification bar) */
  @ContentChild('headerActionsSlot') headerActionsSlotTemplate?: TemplateRef<unknown>;

  /** Custom footer content (above input area) */
  @ContentChild('footerSlot') footerSlotTemplate?: TemplateRef<unknown>;

  /** Custom template for rendering domain-specific content after messages */
  @ContentChild('customContent') customContentTemplate?: TemplateRef<unknown>;

  /** Custom template for rendering domain-specific actions/notifications */
  @ContentChild('customActions') customActionsTemplate?: TemplateRef<unknown>;

  // ============================================================================
  // Outputs
  // ============================================================================

  /** Emitted when user sends a message (with optional image attachments) */
  @Output() sendMessage = new EventEmitter<SendMessagePayload>();

  /** Emitted when user clicks new chat */
  @Output() newChat = new EventEmitter<void>();

  /** Emitted when user cancels request */
  @Output() cancelRequest = new EventEmitter<void>();

  /** Emitted when user clears queued message */
  @Output() clearQueuedMessage = new EventEmitter<void>();

  /** Emitted when user clicks retry on error */
  @Output() retryLastMessage = new EventEmitter<void>();

  /** Emitted when user clicks a message action */
  @Output() messageActionClick = new EventEmitter<MessageActionClickEvent>();

  /** Emitted when user clicks a preset */
  @Output() presetClick = new EventEmitter<StreamingChatPreset>();

  /** Emitted when panel should close */
  @Output() panelClose = new EventEmitter<void>();

  /** Emitted when user approves or rejects a HITL action */
  @Output() hitlApprovalClick = new EventEmitter<{ approval: HitlApprovalInfo; approved: boolean; apiKey?: string }>();

  // ============================================================================
  // ViewChild References
  // ============================================================================

  @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('inputTextarea') inputTextarea?: IonTextarea;

  // ============================================================================
  // Internal State
  // ============================================================================

  /** Current input value */
  inputValue = '';

  /** Pending image attachments (base64 strings) */
  pendingImages: string[] = [];

  /** Reference to hidden file input */
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  /** Whether to scroll to bottom on next view check */
  private shouldScrollToBottom = false;

  /** Force scroll even if user has scrolled up */
  private forceScrollToBottom = false;

  /** Expansion state for thinking sections (keyed by message index) */
  thinkingExpandedState = new Map<number, boolean>();

  /** Expansion state for tool calls (keyed by tool call ID) */
  toolCallExpandedState = new Map<string, boolean>();

  /** Pending timeouts for cleanup */
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = [];

  constructor(private alertController: AlertController) {}

  // ============================================================================
  // Lifecycle
  // ============================================================================

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom || this.forceScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
      this.forceScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    // Clear any pending timeouts
    for (const timeout of this.pendingTimeouts) {
      clearTimeout(timeout);
    }

    this.pendingTimeouts = [];
  }

  // ============================================================================
  // Computed Properties
  // ============================================================================

  /** Get combined messages including streaming message */
  get allMessages(): StreamingChatMessage[] {
    const result = [...this.messages];

    if (this.streamingMessage) {
      result.push(this.streamingMessage);
    }

    return result;
  }

  /** Get effective empty state config */
  get effectiveEmptyState(): StreamingChatEmptyState | undefined {
    if (this.emptyState) {
      return {
        ...this.emptyState,
        presets: this.presets ?? this.emptyState.presets,
      };
    }

    if (this.config.emptyState) {
      return {
        ...this.config.emptyState,
        presets: this.presets ?? this.config.emptyState.presets,
      };
    }

    return undefined;
  }

  /** Get placeholder text */
  get placeholder(): string {
    if (this.isLoading) {
      return 'Processing...';
    }

    return this.config.placeholder ?? 'Type a message...';
  }

  /** Get model hint */
  get modelHint(): string {
    return this.config.modelHint ?? '';
  }

  /** Check if empty state should show */
  get showEmptyState(): boolean {
    return this.messages.length === 0 && !this.streamingMessage && !this.isLoading;
  }

  /** Check if messages area should show */
  get showMessagesArea(): boolean {
    return this.messages.length > 0 || !!this.streamingMessage || this.isLoading;
  }

  /** True when the composer has at least one character (fades in keyboard/model hints). */
  get showInputFooterHints(): boolean {
    return (this.inputValue?.length ?? 0) > 0;
  }

  // ============================================================================
  // Input Handling
  // ============================================================================

  onInputKeyDown(event: KeyboardEvent): void {
    // Enter without shift sends the message
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSendClick();
    }
  }

  onSendClick(): void {
    const message = this.inputValue?.trim();

    if (!message && !this.pendingImages.length) {
      return;
    }

    this.sendMessage.emit({
      text: message || '',
      images: this.pendingImages.length > 0 ? [...this.pendingImages] : undefined,
    });
    this.inputValue = '';
    this.pendingImages = [];
    this.requestScrollToBottom(true);
  }

  // ============================================================================
  // Image Attachment Handling
  // ============================================================================

  /** Whether the user is dragging files over the input area */
  isDragging = false;

  onAttachClick(): void {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) {
      return;
    }

    this.processImageFiles(Array.from(input.files));

    // Reset file input so the same file can be selected again
    input.value = '';
  }

  removeImage(index: number): void {
    this.pendingImages = this.pendingImages.filter((_, i) => i !== index);
  }

  // ============================================================================
  // Drag and Drop
  // ============================================================================

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;

    if (!files?.length) {
      return;
    }

    this.processImageFiles(Array.from(files));
  }

  /** Shared logic for processing image files from file input or drag-and-drop */
  private processImageFiles(files: File[]): void {
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        continue;
      }

      // Max 10MB per image
      if (file.size > 10 * 1024 * 1024) {
        continue;
      }

      const reader = new FileReader();

      reader.onload = () => {
        const base64 = reader.result as string;
        this.pendingImages = [...this.pendingImages, base64];
      };

      reader.readAsDataURL(file);
    }
  }

  // ============================================================================
  // Quick Actions / Presets
  // ============================================================================

  onPresetClick(preset: StreamingChatPreset): void {
    this.presetClick.emit(preset);
    // Also send as message for convenience
    this.sendMessage.emit({ text: preset.message });
    this.requestScrollToBottom(true);
  }

  // ============================================================================
  // Chat Actions
  // ============================================================================

  async onNewChatClick(): Promise<void> {
    if (this.messages.length === 0 && !this.streamingMessage) {
      this.newChat.emit();

      return;
    }

    const alert = await this.alertController.create({
      header: 'Start New Chat',
      message: 'This will clear the current conversation. Continue?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'New Chat',
          role: 'confirm',
          handler: () => {
            this.newChat.emit();
          },
        },
      ],
    });

    await alert.present();
  }

  onCancelClick(): void {
    this.cancelRequest.emit();
  }

  onClearQueuedMessage(): void {
    this.clearQueuedMessage.emit();
  }

  onRetryClick(): void {
    this.retryLastMessage.emit();
  }

  closePanel(): void {
    this.panelClose.emit();
  }

  // ============================================================================
  // Message Actions
  // ============================================================================

  onMessageActionClick(action: StreamingChatMessageAction, messageIndex: number): void {
    this.messageActionClick.emit({ action, messageIndex });
  }

  // ============================================================================
  // Thinking Section
  // ============================================================================

  toggleThinkingExpanded(messageIndex: number | undefined): void {
    if (messageIndex === undefined) {
      return;
    }

    const current = this.thinkingExpandedState.get(messageIndex) ?? false;
    this.thinkingExpandedState.set(messageIndex, !current);
  }

  isThinkingExpanded(messageIndex: number | undefined): boolean {
    if (messageIndex === undefined) {
      return false;
    }

    return this.thinkingExpandedState.get(messageIndex) ?? false;
  }

  // ============================================================================
  // Tool Calls
  // ============================================================================

  toggleToolCallExpanded(toolCallId: string): void {
    const current = this.toolCallExpandedState.get(toolCallId) ?? false;
    this.toolCallExpandedState.set(toolCallId, !current);
  }

  isToolCallExpanded(toolCallId: string): boolean {
    return this.toolCallExpandedState.get(toolCallId) ?? false;
  }

  hasToolCallDetails(toolCall: ToolCallInfo): boolean {
    return toolCall.input !== undefined && toolCall.input !== null;
  }

  getToolCallDetails(toolCall: ToolCallInfo): string {
    try {
      return JSON.stringify(toolCall.input, null, 2);
    } catch {
      return String(toolCall.input);
    }
  }

  getToolCallLabel(toolName: string, input?: unknown): string {
    // Use custom resolver if provided
    if (this.config.getToolCallLabel) {
      return this.config.getToolCallLabel(toolName, input);
    }

    // Default: humanize tool name
    return toolName
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase());
  }

  shouldShowToolCall(toolName: string): boolean {
    // Use custom filter if provided
    if (this.config.shouldShowToolCall) {
      return this.config.shouldShowToolCall(toolName);
    }

    // Default: show all tool calls
    return true;
  }

  // ============================================================================
  // Scroll Management
  // ============================================================================

  requestScrollToBottom(force = false): void {
    this.shouldScrollToBottom = true;
    this.forceScrollToBottom = force;
  }

  private scrollToBottom(): void {
    if (!this.messagesContainer?.nativeElement) {
      return;
    }

    const container = this.messagesContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
  }

  focusInput(): void {
    this.inputTextarea?.setFocus();
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  formatDuration(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`;
    }

    const seconds = Math.round(ms / 100) / 10;

    return `${seconds}s`;
  }

  trackMessage(index: number, msg: StreamingChatMessage): string {
    return `${index}-${msg.role}-${msg.content?.slice(0, 20)}`;
  }

  trackAction(index: number, action: StreamingChatMessageAction): string {
    return action.id;
  }

  /** Schedule a timeout and track it for cleanup */
  scheduleTimeout(fn: () => void, delay: number): void {
    const timeout = setTimeout(() => {
      // Remove from tracked timeouts
      const idx = this.pendingTimeouts.indexOf(timeout);

      if (idx >= 0) {
        this.pendingTimeouts.splice(idx, 1);
      }

      fn();
    }, delay);

    this.pendingTimeouts.push(timeout);
  }
}
